import type { CostRate, CostRateKind, CostRates } from '../types/cost-rates.types';

/** The options that change what a run costs. */
export interface RunCostOptions {
  /** Products the run will process. */
  products: number;
  /** Content runs whenever any attribute is selected (one call covers them all). */
  content: boolean;
  searchDescriptors: boolean;
  phraseDescriptions: boolean;
  scoringBefore: boolean;
  scoringAfter: boolean;
  /** Languages besides English. */
  languages: number;
}

/** One priced line of the run's cost. */
export interface RunCostLine {
  kind: CostRateKind;
  /** i18n key suffix under `runWizard.cost.lines`. */
  labelKey: string;
  /** Per product (per product and language for translation, times languages); null = unknown. */
  perProductUsd: number | null;
  /** Line total; null when its rate is unknown. */
  totalUsd: number | null;
  rate: CostRate | null;
  /**
   * Scoring only: products the pipeline's own scoring stage scores on top of every product (the
   * per-product step), at the same rate. 0 for every other line.
   */
  stageProducts: number;
  /** Content only: the sample's products, priced at the sample rate, inside this line. */
  sampleProducts: number;
  /** Scoring only, once workflow caps it: products scored per phase (null = every product). */
  scoredProducts: number | null;
}

export interface RunCost {
  lines: RunCostLine[];
  /** Sum of the lines whose rate is known. */
  totalUsd: number;
  perProductUsd: number;
  /** Some chosen option has no rate: the total leaves it out. */
  hasUnknown: boolean;
  /** Some rate behind the total rests on few products. */
  lowConfidence: boolean;
}

const ORDER: { kind: CostRateKind; labelKey: string; on: (o: RunCostOptions) => number }[] = [
  { kind: 'CONTENT', labelKey: 'content', on: (o) => (o.content ? 1 : 0) },
  {
    kind: 'SEARCH_DESCRIPTORS',
    labelKey: 'searchDescriptors',
    on: (o) => (o.searchDescriptors ? 1 : 0),
  },
  {
    kind: 'PHRASE_DESCRIPTIONS',
    labelKey: 'phraseDescriptions',
    on: (o) => (o.searchDescriptors && o.phraseDescriptions ? 1 : 0),
  },
  { kind: 'SCORING_PRE', labelKey: 'scoringBefore', on: (o) => (o.scoringBefore ? 1 : 0) },
  { kind: 'SCORING_POST', labelKey: 'scoringAfter', on: (o) => (o.scoringAfter ? 1 : 0) },
  { kind: 'TRANSLATION', labelKey: 'translation', on: (o) => o.languages },
];

const SCORING = new Set<CostRateKind>(['SCORING_PRE', 'SCORING_POST']);

/** The rate for one kind, or null when the server has none. */
export function rateOf(rates: CostRates | undefined, kind: CostRateKind): CostRate | null {
  return rates?.rates.find((r) => r.kind === kind) ?? null;
}

/**
 * The run's cost: each chosen option's per-product rate times the products (and languages, for
 * translation). An option whose rate is unknown is listed but left out of the total, never priced
 * at zero.
 */
export function runCost(rates: CostRates | undefined, options: RunCostOptions): RunCost {
  const lines: RunCostLine[] = [];
  for (const { kind, labelKey, on } of ORDER) {
    const times = on(options);
    if (times === 0) continue;
    const rate = rateOf(rates, kind);
    // Scope NONE means nothing was recorded: unknown, never free.
    const per = rate && rate.scope !== 'NONE' ? rate.perProductUsd : null;
    const scoring = SCORING.has(kind);
    // Capped scoring (sample + the full run's share, the same ceiling in every environment) once
    // workflow reports it; until then a phase scores every product plus the stage's own sample.
    const scoredProducts =
      scoring && rates?.scoredProductsPerPhase !== undefined
        ? Math.min(options.products, rates.scoredProductsPerPhase)
        : null;
    const stageProducts =
      scoring && scoredProducts === null
        ? Math.min(options.products, rates?.scoringStageProducts ?? 0)
        : 0;
    // Content: the sample's products at the sample rate (they are not enriched again), the rest at
    // the full-run rate — both belong to the same batch.
    const sampleRate = kind === 'CONTENT' ? (rates?.sampleContent ?? null) : null;
    const samplePer = sampleRate && sampleRate.scope !== 'NONE' ? sampleRate.perProductUsd : null;
    const sampleProducts =
      samplePer === null ? 0 : Math.min(options.products, rates?.sampleSize ?? 0);
    let total: number | null = null;
    if (per !== null) {
      if (scoredProducts !== null) total = per * scoredProducts * times;
      else if (samplePer !== null && sampleProducts > 0)
        total = (samplePer * sampleProducts + per * (options.products - sampleProducts)) * times;
      else total = per * (options.products + stageProducts) * times;
    }
    lines.push({
      kind,
      labelKey,
      rate,
      stageProducts,
      sampleProducts,
      scoredProducts,
      // What one product of this run costs for the line, the sample and stage shares spread over it.
      perProductUsd: total === null || options.products === 0 ? null : total / options.products,
      totalUsd: total,
    });
  }
  const known = lines.filter((l) => l.totalUsd !== null);
  const totalUsd = known.reduce((n, l) => n + (l.totalUsd ?? 0), 0);
  return {
    lines,
    totalUsd,
    perProductUsd: known.reduce((n, l) => n + (l.perProductUsd ?? 0), 0),
    hasUnknown: lines.length > known.length,
    lowConfidence: lines.some((l) => l.rate?.lowConfidence === true),
  };
}

/** USD for display: cents below $100, whole dollars above; a non-zero sub-cent amount is "<$0.01". */
export function formatUsd(value: number): string {
  if (value > 0 && value < 0.005) return '<$0.01';
  return value >= 100
    ? `$${Math.round(value).toLocaleString()}`
    : `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** A per-product rate: four decimals, since most are fractions of a cent. */
export function formatRate(value: number): string {
  return `$${value.toFixed(4)}`;
}
